import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { blockedUserCount, tenantsListPath, tenantsOf, TIER_UNSET, type McpTenantList } from "../../api/mcp";
import { CouldNotLook, Denied, LoadFailed, Loading, SignedOut } from "../../components/states";
import { Badge, Count, Empty, FilterBar, Notice, Page, Panel, SearchField, SelectField, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { Button, Input } from "../../f7";
import { useResource } from "../../hooks/useResource";
import { ProductScope, StoredAs } from "../../products/PerProduct";
import {
  LABEEB_TENANTS,
  labeebTenantsOf,
  type LabeebTenantList,
  type LabeebTenantSummary,
} from "../../api/labeeb";
import {
  labeebSuspended,
  mergeTenants,
  nameOf,
  searchTextOf,
  type MergedTenant,
} from "./mergeTenants";

export function Tenants({ config }: { config: AppConfig }) {
  const [q, setQ] = useState("");
  const [lookup, setLookup] = useState("");
  // "" = every tier, TIER_UNSET = merchants with no entitlements row.
  // Named tiers go on the wire as ?tier=. TIER_UNSET cannot: the server matches
  // the stored string exactly, and a tier-less merchant stores null.
  const [tier, setTier] = useState("");
  const { state, reload } = useResource<McpTenantList>(config, tenantsListPath(tier), [tier]);

  // Labeeb's side, fetched ONCE and MERGED — not merely joined. Its rows are
  // merchants in their own right: a merchant with a Labeeb config item and no
  // MCP history used to be absent from this table entirely.
  const labeebList = useResource<LabeebTenantList>(config, LABEEB_TENANTS);
  const labeebRows: LabeebTenantSummary[] =
    labeebList.state.kind === "ready" ? labeebTenantsOf(labeebList.state.data) : [];

  const navigate = useNavigate();

  return (
    <Page
      title="All tenants"
      subtitle="Every merchant this console can see, and what each product stores for them."
    >
      {/* WHOSE LIST THIS IS. It comes from GET /mcp/admin/tenants, so it holds
          merchants that have used MCP. A merchant using Labeeb and not MCP is
          absent from this table entirely — not shown as inactive, ABSENT — and
          that is invisible without saying it. The per-row cells can only flag a
          missing Labeeb value for a merchant already in the list; nothing can
          flag a merchant who never enters it. */}
      <ProductScope owns={["mcp"]}>
        the list is MCP&rsquo;s verification ledger — a merchant who uses only Labeeb does not
        appear here at all
      </ProductScope>

      {/* The list only holds merchants seen in the last year, so a dormant one
          is absent and otherwise unreachable — yet it can still be blocked.
          Without this field the 404 state would be decorative. */}
      <Panel className="p-4">
        <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
          Open any merchant by reference, including one not listed below:
        </p>
        <form
          className="flex flex-wrap items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            const v = lookup.trim();
            if (v) navigate(`/tenants/${encodeURIComponent(v)}`);
          }}
        >
          {/* The width goes on a WRAPPER. F7's Input renders inside its own
              `w-full` span, so `className="w-56"` on the Input was overridden
              and the field filled the panel — pushing Open onto its own line
              and making a one-field lookup two rows tall. */}
          <div className="w-64">
            <Input
              value={lookup}
              onChange={(e) => setLookup(e.target.value)}
              placeholder="Business reference, e.g. 507077"
              aria-label="Business reference"
            />
          </div>
          <Button type="submit" disabled={!lookup.trim()}>
            Open
          </Button>
        </form>
      </Panel>

      {state.kind === "loading" && <Loading what="merchants" />}
      {state.kind === "signedout" && <SignedOut />}
      {state.kind === "denied" && <Denied message={state.error.message} />}
      {state.kind === "unavailable" && (
        <CouldNotLook what="the merchant list" message={state.error.message} onRetry={reload} />
      )}
      {state.kind === "notfound" && <LoadFailed what="merchants" message="Route not found." />}
      {state.kind === "invalid" && (
        <LoadFailed what="merchants" message={state.error.message} />
      )}
      {state.kind === "error" && <LoadFailed what="merchants" message={state.error.message} />}

      {state.kind === "ready" &&
        (() => {
          const all: MergedTenant[] = mergeTenants(tenantsOf(state.data), labeebRows);
          const everywhere = state.data.users_blocked_across_all_merchants ?? [];
          // Named tiers are already narrowed by the server. TIER_UNSET is the
          // one slice ?tier= cannot express — filter the unfiltered list here.
          //
          // Both filters read MCP's tier: the ?tier= parameter is MCP's, so a
          // Labeeb-only merchant has no MCP tier to match and drops out of a
          // filtered view. That is correct — it is not on that MCP tier — and
          // it is why the unfiltered list is the one that shows everything.
          const byTier =
            tier === TIER_UNSET ? all.filter((r) => r.mcp?.tier == null) : all;
          const rows = q
            ? byTier.filter((r) => searchTextOf(r).includes(q.toLowerCase()))
            : byTier;

          return (
            <>
              <FilterBar>
                <SearchField
                  value={q}
                  onChange={setQ}
                  placeholder="Reference, name, email, or a Labeeb/entitlements id…"
                  label="Filter merchants"
                />
                <SelectField value={tier} onChange={setTier} label="Filter by tier" width={150}>
                  <option value="">Every tier</option>
                  {(state.data.known_tiers ?? []).map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                  <option value={TIER_UNSET}>not set</option>
                </SelectField>
                <Count shown={rows.length} total={all.length} />
              </FilterBar>

              {rows.length === 0 && (
                <Empty>
                  {q
                    ? "Nothing matches that filter."
                    : tier
                      ? "No merchants on that tier."
                      : "No merchants have connected yet."}
                </Empty>
              )}

              {/* NOT dense. Every row here carries two lines in three of its
                  cells plus an activity cell — the one table in the design
                  left at full height, because dense squeezes the second line
                  into the first and the reference under the name stops being
                  readable. */}
              {rows.length > 0 && (
              <Table
                dense={false}
                head={
                  <tr>
                    <Th>Reference</Th>
                    <Th>Name</Th>
                    {/* ONE tier column, carrying both products' values on
                        their own lines. A merchant holds a tier in EACH
                        product and the pair can disagree — a `tier split` —
                        which a single flattened value cannot show. This used
                        to be two columns, "Tier" and "Stored as", saying the
                        same thing twice.

                        Email, Agents and Activity are gone: an address belongs
                        on the merchant page, agents are a Labeeb detail shown
                        there too, and "Activity · 30d" was a blurred bar bound
                        to no data at all — neither product serves a usage
                        history route. */}
                    <Th>Tier</Th>
                    <Th>Status</Th>
                  </tr>
                }
                    >
                {rows.map((m) => (
                  // Ops know the numeric reference; the UUID is internal and
                  // never surfaced — it is not even in the type.
                  <tr key={m.reference} className="hover:bg-slate-950/[2.5%] dark:hover:bg-white/5">
                    <Td>
                      <Link
                        to={`/tenants/${encodeURIComponent(m.reference)}`}
                        className="ref"
                      >
                        {m.reference}
                      </Link>
                    </Td>
                    <Td>
                      {/* Labeeb ships a name too — its list attaches
                          business_name from the BusinessProfile item. The
                          console used to declare neither field and render
                          every Labeeb-only merchant as a bare em dash. */}
                      <span className="flex flex-wrap items-center gap-1.5">
                        {nameOf(m) ?? (
                          <span className="text-slate-400 dark:text-slate-500">—</span>
                        )}
                        {!m.mcp && <Badge tone="neutral">Labeeb only</Badge>}
                        {/* "Configured, never used" is gone: it detected a
                            config row with no profile, which was the shape of
                            an accidental enrolment. The new API has no such
                            row to create — a quota write 404s on a business
                            Labeeb does not know — so the badge had nothing
                            left to warn about.
                            Suspension replaces it, and is the state actually
                            worth catching while scanning a list. */}
                        {labeebSuspended(m) && <Badge tone="red">Labeeb suspended</Badge>}
                      </span>
                    </Td>
                    <Td>
                      {/* Both products on their own lines. null is NOT "free":
                          the server resolves an absent row as free, but
                          nothing has been stored, and rendering "free" would
                          claim a decision nobody made. StoredAs prints "no
                          row" for exactly that case. */}
                      {/* Labeeb's tier is no longer on the fleet list — it is
                          a per-business document now, and fetching one per row
                          would be N requests for a column nobody scans a list
                          for. MCP's is still here; Labeeb's lives on the
                          merchant page beside the control that changes it. */}
                      <StoredAs stored={{ mcp: m.mcp?.tier ?? null, labeeb: null }} />
                    </Td>
                    <Td>
                      {/* PER PRODUCT, and named as such. The old badge said
                          "Active", which claimed more than the console knows:
                          `blocked` is MCP's blocklist and nothing here can see
                          whether Labeeb has suspended the same merchant. A
                          merchant suspended in Labeeb and clear in MCP would
                          have read as simply Active. */}
                      {/* One badge per product that HAS this merchant.
                          "Labeeb not wired" used to sit on every row saying
                          the same thing eight times — a constant is not
                          information. Labeeb has no suspension route, so it
                          has no status to report; the absence of a Labeeb
                          badge is the honest rendering of that, and the one
                          place it is worth spelling out is the merchant's own
                          Access section. */}
                      <div className="flex flex-wrap items-center gap-1">
                        {m.mcp ? (
                          m.mcp.blocked ? (
                            <Badge tone="red">MCP blocked</Badge>
                          ) : (
                            <Badge tone="green">MCP active</Badge>
                          )
                        ) : (
                          // MCP has never seen this merchant, so it is neither
                          // blocked nor active there — a third state, and
                          // painting it green would invent a fact.
                          <span className="text-xs text-slate-400 dark:text-slate-500">
                            not in MCP
                          </span>
                        )}
                        {/* Labeeb appears only when it knows the merchant. It
                            cannot say blocked-or-not, so it says which product
                            holds a config for them. */}
                        {m.labeeb && <Badge tone="neutral">Labeeb</Badge>}
                      </div>
                      {m.mcp && blockedUserCount(m.mcp) > 0 && (
                        <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">
                          {blockedUserCount(m.mcp)} user
                          {blockedUserCount(m.mcp) === 1 ? "" : "s"} blocked
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </Table>
              )}

              {/* NOT a property of any merchant above — these are ("*", email)
                  rows, blocked everywhere. Reported separately for that reason.

                  No `mt-4`: the column this sits in is the Page's own
                  `gap-6`, and a margin on top of it made the gap under the
                  table half again as large as every other gap on the page. */}
              {everywhere.length > 0 && (
                <Notice tone="amber" title="Blocked across all merchants">
                  <p className="text-xs">
                    These are not attached to any merchant in the list — they apply
                    everywhere.
                  </p>
                  <ul className="mt-1 space-y-0.5">
                    {everywhere.map((e) => (
                      <li key={e} className="ref text-xs">
                        {e}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-2 text-xs">
                    <Link to="/policy/access" className="underline">
                      Manage blocks
                    </Link>
                  </p>
                </Notice>
              )}

              <p className="text-xs text-slate-500 dark:text-slate-400">
                {rows.length} shown
                {q && byTier.length !== rows.length ? ` of ${byTier.length}` : ""}.
                Merchants appear here from the verification ledger, whose entries expire
                after a year — a dormant merchant is absent but can still be opened by
                reference above.
              </p>
            </>
          );
        })()}
    </Page>
  );
}
